import { forwardRef, useState } from 'react';
import { Drawer as AntdDrawer } from 'antd';
import { FocusTrap } from 'focus-trap-react';
import { CloseBoldIcon } from '@solar-icons/react';
import clsx from 'clsx';
import styles from './Drawer.module.css';

// AntdDrawer isn't ref-forwardable; FocusTrap clones its child with a ref regardless, which
// otherwise warns. The ref itself is unused here — `containerElements` below is the real trap
// boundary — so this just accepts and drops it, with no extra DOM node.
const RefTrapTarget = forwardRef((props, _ref) => <AntdDrawer {...props} />);

const DEFAULT_ARIA_LABEL = 'Navigation drawer';

/**
 * @param {Object} props - Same shape as antd's own `Drawer`.
 * @param {string} [props.aria-label] - Falls back to "Navigation drawer" when no `title` is
 *   given; has no effect once a `title` is passed.
 * @param {boolean} [props.hideHeader] - Drops the header and its close button, and ignores
 *   `title`; the consumer must supply its own way to close the drawer.
 * @param {boolean} [props.fullWidthOnMobile] - Makes the drawer span the full viewport width
 *   below 768px.
 * @param {*} [props.closeIcon] - Not used — the close icon is fixed and can't be overridden.
 * @param {*} [props.closable] - Not used — the close button's accessible label is fixed and
 *   can't be overridden.
 */
const Drawer = forwardRef((
    {
        className,
        rootClassName,
        open,
        afterOpenChange,
        title: titleProp,
        'aria-label': ariaLabel,
        closeIcon,
        closable,
        hideHeader,
        fullWidthOnMobile,
        ...props
    },
    ref
) => {
    const [panelElement, setPanelElement] = useState(null);
    const [trapActive, setTrapActive] = useState(false);
    const title = hideHeader ? undefined : titleProp;

    return (
        <FocusTrap
            active={open && trapActive}
            containerElements={panelElement ? [panelElement] : []}
        >
            <RefTrapTarget
                ref={ref}
                className={clsx(styles.vecDrawer, className)}
                rootClassName={clsx(fullWidthOnMobile && styles.fullWidthOnMobile, rootClassName)}
                open={open}
                panelRef={setPanelElement}
                focusable={{ trap: false }}
                title={title}
                aria-label={title ? undefined : (ariaLabel ?? DEFAULT_ARIA_LABEL)}
                closeIcon={<CloseBoldIcon size={32} color="var(--color-surface-brand)" />}
                closable={hideHeader ? false : { 'aria-label': 'Close' }}
                afterOpenChange={visible => {
                    setTrapActive(visible);
                    afterOpenChange?.(visible);
                }}
                {...props}
            />
        </FocusTrap>
    );
});
Drawer.displayName = 'Drawer';

export default Drawer;
